import type { ShipmentTemplate } from "types/shipment";

export const shipmentTemplatesListUrl = (shipperRoleId: number): string =>
  `/shippers/${shipperRoleId}/shipment-templates`;

// Matches the SWR cache key for any shipper's shipment-templates list, so a newly
// created template can revalidate the list via global mutate without the role id.
export const isShipmentTemplatesListKey = (key: unknown): boolean =>
  typeof key === "string" && key.endsWith("/shipment-templates");

/**
 * Filters by search query and sorts template items by creation date
 * Searching:
 * - Searches across template name
 * - Searches across origin address (first route point)
 * - Searches across destination address (last route point)
 * - Case-insensitive substring matching
 * Sorting:
 * - Always sorts by created_at timestamp (newest first)
 * @param templates Array of shipment templates to filter and sort
 * @param searchQuery Text to filter by (searches name, origin, destination)
 * @returns Filtered and sorted array of templates
 */
export function filterAndSortTemplates(
  templates: ShipmentTemplate[],
  searchQuery: string,
): ShipmentTemplate[] {
  // First filter by search query
  let filtered = templates;
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    filtered = templates.filter((template) => {
      const templateName = template.name.toLowerCase();
      const route = template.content.route ?? [];

      const originAddress = route[0]?.point_location?.city?.toLowerCase() ?? "";
      const lastRouteIndex = route.length - 1;
      const destinationAddress =
        route.length > 1
          ? (route[lastRouteIndex]?.point_location?.city?.toLowerCase() ?? "")
          : "";

      return (
        templateName.includes(query) ||
        originAddress.includes(query) ||
        destinationAddress.includes(query)
      );
    });
  }

  // Sort the filtered results by creation date (newest first)
  return [...filtered].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}
